import React, { useState, useEffect, useRef } from "react";
import {
  ChatAlt2Icon,
  PhoneIcon,
  MailIcon,
  PaperAirplaneIcon,
  PlusIcon,
  XIcon,
} from "@heroicons/react/solid";
import { useSidebar } from "../../context/SidebarContext";
import { useCommunication } from "../../hooks/useCommunication";
import { useVoiceCall } from "../../hooks/useVoiceCall";
import { userAPI } from "../../services/userAPI";
import { useAuth } from "../../context/AuthContext";

const CommunicationCenter = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const [showMobileChat, setShowMobileChat] = useState(false);
  const { user } = useAuth();
  const {
    conversations,
    currentConversation,
    messages,
    loading,
    error,
    isConnected,
    typingUsers,
    messagesEndRef,
    fetchConversations,
    sendMessage,
    selectConversation,
    setTyping,
    createConversation,
    clearError,
    getUnreadCount,
    isUserOnline,
    markConversationAsRead,
  } = useCommunication();

  const [searchQuery, setSearchQuery] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userError, setUserError] = useState(null);

  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch conversations on component mount
  useEffect(() => {
    console.log("Credit Manager fetching conversations with user:", {
      userId: user._id,
      userName: user.name,
      userType: user.userType,
    });
    fetchConversations();
  }, [fetchConversations, user]);

  // Fetch available users when new chat modal opens
  useEffect(() => {
    if (showNewChat) {
      const fetchUsers = async () => {
        try {
          setLoadingUsers(true);
          setUserError(null);
          console.log("Fetching users for new conversation...");

          // Fetch banking partners and admins specifically for credit manager
          const [bankingPartnersResponse, adminsResponse] = await Promise.all([
            userAPI.getUsersByType("banking-partner"),
            userAPI.getUsersByType("admin"),
          ]);

          console.log("Banking partners response:", bankingPartnersResponse);
          console.log("Admins response:", adminsResponse);

          // Combine and filter out current user
          console.log("Current user from useAuth:", user);

          const allUsers = [
            ...(bankingPartnersResponse?.users || []),
            ...(adminsResponse?.users || []),
          ];

          console.log("All users before filtering:", allUsers);

          const filteredUsers = allUsers.filter(
            (userItem) => userItem._id !== user._id && userItem._id !== user.id
          );

          console.log("Filtered users:", filteredUsers);
          setAvailableUsers(filteredUsers);
        } catch (error) {
          console.error("Failed to fetch users:", error);
          setUserError("Failed to fetch users. Please try again.");

          // Try alternative approach if the first one fails
          try {
            console.log("Trying alternative user fetching approach...");
            const allUsersResponse = await userAPI.getAllUsers();

            const filteredUsers =
              allUsersResponse.users?.filter(
                (userItem) =>
                  (userItem.userType === "banking-partner" ||
                    userItem.userType === "admin") &&
                  userItem._id !== user._id &&
                  userItem._id !== user.id
              ) || [];

            console.log("Alternative filtered users:", filteredUsers);
            setAvailableUsers(filteredUsers);
            setUserError(null);
          } catch (altError) {
            console.error("Alternative approach also failed:", altError);
            setUserError("Unable to load users. Please check your connection.");
          }
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [showNewChat]);

  // Handle typing indicator
  useEffect(() => {
    if (currentConversation) {
      setTyping(currentConversation.conversationId, isTyping);
    }
  }, [isTyping, currentConversation, setTyping]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, messagesEndRef]);

  // Handle message input changes
  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Update typing indicator
    if (value.length > 0 && !isTyping) {
      setIsTyping(true);
    } else if (value.length === 0 && isTyping) {
      setIsTyping(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentConversation) return;

    try {
      console.log("Sending message with user:", {
        userId: user._id,
        userName: user.name,
        message: newMessage.trim(),
      });

      await sendMessage(currentConversation.conversationId, newMessage.trim());
      setNewMessage("");
      setIsTyping(false);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  // Handle enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Update showMobileChat when conversation is selected
  useEffect(() => {
    if (currentConversation && isMobile) {
      setShowMobileChat(true);
    }
  }, [currentConversation, isMobile]);

  // Mark conversation as read when messages are viewed
  useEffect(() => {
    if (currentConversation && messages.length > 0) {
      markConversationAsRead(
        currentConversation.conversationId || currentConversation._id
      );
    }
  }, [currentConversation, messages, markConversationAsRead]);

  // Create new conversation
  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) return;

    try {
      const participants = selectedUsers.map((user) => ({
        userId: user._id,
        userType: user.userType,
      }));

      const response = await createConversation(participants, "direct");
      setShowNewChat(false);
      setSelectedUsers([]);

      // Select the new conversation
      if (response.conversation) {
        selectConversation(response.conversation);
      }
    } catch (error) {
      console.error("Failed to create conversation:", error);
    }
  };

  // Filter conversations based on search
  const filteredConversations = conversations.filter(
    (conv) =>
      conv.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.participants.some((p) =>
        p.userId?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  // Get conversation title
  const getConversationTitle = (conversation) => {
    if (conversation.title && conversation.title !== "Direct Message") {
      return conversation.title;
    }

    const otherParticipants = conversation.participants.filter((p) => {
      const participantId =
        typeof p.userId === "string" ? p.userId : p.userId?._id;
      return participantId !== user._id && participantId !== user.id;
    });

    if (otherParticipants.length === 1) {
      return otherParticipants[0]?.userId?.name || "Unknown User";
    } else if (otherParticipants.length > 1) {
      return `${otherParticipants[0]?.userId?.name || "User"} +${
        otherParticipants.length - 1
      }`;
    }

    return "Direct Message";
  };

  // Get conversation preview
  const getConversationPreview = (conversation) => {
    if (conversation.lastMessage?.content) {
      let content = "";

      if (typeof conversation.lastMessage.content === "string") {
        content = conversation.lastMessage.content;
      } else if (conversation.lastMessage.content?.text) {
        content = conversation.lastMessage.content.text;
      } else if (conversation.lastMessage.content?.message) {
        content = conversation.lastMessage.content.message;
      }

      if (content && content.trim() !== "") {
        return content.length > 50 ? content.substring(0, 50) + "..." : content;
      }
    }
    return "No messages yet";
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString();
    }
  };

  // Check if user is online (using the hook function)
  const checkUserOnline = (userId) => {
    return isUserOnline(userId);
  };

  return (
    <div
      className="max-h-screen bg-black w-full text-white mt-12 overflow-hidden"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "240px" : "90px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "240px" : "90px"})`,
      }}
    >
      {/* Header */}
      <div className="bg-black p-2 md:p-4 flex justify-between items-center w-full">
        <h1 className="text-lg font-medium ml-1 md:ml-9">
          Communication Center
        </h1>
        <div className="flex items-center space-x-2">
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected ? "bg-green-500" : "bg-red-500"
            }`}
          ></div>
          <span className="text-sm text-gray-400">
            {isConnected ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      <div className="flex flex-col bg-[#131313] border-0 md:border md:border-opacity-40 md:border-white rounded-lg p-0 md:p-5 mx-0 md:mx-12 h-[calc(100vh-9rem)]">
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden h-full">
          {/* Conversations Sidebar - Hidden on mobile when chat is active */}
          <div
            className={`${isMobile && showMobileChat ? "hidden" : "block"} ${
              isMobile ? "w-full h-full" : "w-80"
            } bg-[#131313] ${
              !isMobile
                ? "border border-opacity-40 border-white rounded-lg md:mr-3"
                : ""
            } mb-0 overflow-hidden`}
          >
            {/* Search and New Chat */}
            <div className="p-4 border-b border-[#131313] flex flex-row items-center justify-between sticky top-0 bg-[#131313] z-10">
              {/* Left side: Conversations */}
              <div className="text-white text-base font-medium">
                Conversations
              </div>

              {/* Right side: New Chat Button */}
              <div className="flex items-center ">
                <button
                  onClick={() => setShowNewChat(true)}
                  className="flex items-center space-x-2 bg-[#ffca20] hover:bg-[#ffca20]/80 text-black px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>New</span>
                </button>
              </div>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto h-full">
              {loading ? (
                <div className="p-4 text-center text-gray-400">
                  Loading conversations...
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-4 text-center text-gray-400">
                  No conversations found
                </div>
              ) : (
                filteredConversations.map((conversation) => {
                  const conversationId =
                    conversation.conversationId || conversation._id;
                  const unreadCount = getUnreadCount(conversationId) || 0;
                  const hasOnlineUser = conversation.participants.some((p) => {
                    // Handle different participant structures
                    let participantId;
                    if (typeof p.userId === 'string') {
                      participantId = p.userId;
                    } else if (p.userId && p.userId._id) {
                      participantId = p.userId._id;
                    } else if (p._id) {
                      participantId = p._id;
                    }
                    
                    const currentUserId = user._id || user.id;
                    const isOtherUser = participantId && participantId !== currentUserId;
                    const isOnline = isOtherUser ? checkUserOnline(participantId) : false;

                    console.log(`🔍 Checking participant:`, {
                      participantId,
                      currentUserId,
                      isOtherUser,
                      isOnline,
                      conversationId
                    });

                    return isOtherUser && isOnline;
                  });

                  return (
                    <div
                      key={conversationId}
                      onClick={() => {
                        selectConversation(conversation);
                        if (isMobile) setShowMobileChat(true);
                      }}
                      className={`p-4 border-b border-[#131313] cursor-pointer hover:bg-[#2a2a2a] transition-colors ${
                        currentConversation?.conversationId ===
                          conversationId ||
                        currentConversation?._id === conversationId
                          ? "bg-[#2a2a2a] border-l-4 border-l-[#ffca20]"
                          : ""
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium text-white truncate">
                              {getConversationTitle(conversation)}
                            </h3>
                            {hasOnlineUser && (
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                          </div>
                          <p className="text-sm text-gray-400 truncate mt-1">
                            {getConversationPreview(conversation)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          <div className="text-xs text-gray-500">
                            {formatTimestamp(
                              conversation.lastMessage?.timestamp
                            )}
                          </div>
                          {unreadCount > 0 && (
                            <div className="bg-[#ffca20] text-black text-xs font-bold rounded-full min-h-[20px] min-w-[20px] px-2 flex items-center justify-center">
                              {unreadCount > 99 ? "99+" : unreadCount}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div
            className={`flex-1 flex flex-col border-0 md:border md:border-opacity-40 md:border-white rounded-lg bg-[#131313] overflow-hidden ${
              isMobile && !showMobileChat ? "hidden" : ""
            }`}
          >
            {currentConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-2 sm:p-3 md:p-4 border-b border-[#131313] flex items-center justify-between sticky top-0 bg-[#131313] z-10">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    {isMobile && (
                      <button
                        onClick={() => setShowMobileChat(false)}
                        className="text-white"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    )}
                    <div className="w-7 h-7 sm:w-9 sm:h-9 bg-[#ffca20] rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-xs">
                        {getConversationTitle(
                          currentConversation
                        )[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h2 className="font-medium text-white text-sm sm:text-base">
                          {getConversationTitle(currentConversation)}
                        </h2>
                        {currentConversation.participants.some((p) => {
                          // Handle different participant structures
                          let participantId;
                          if (typeof p.userId === 'string') {
                            participantId = p.userId;
                          } else if (p.userId && p.userId._id) {
                            participantId = p.userId._id;
                          } else if (p._id) {
                            participantId = p._id;
                          }
                          
                          const currentUserId = user._id || user.id;
                          const isOtherUser = participantId && participantId !== currentUserId;
                          const isOnline = isOtherUser ? checkUserOnline(participantId) : false;

                          console.log(`🔍 Chat Header - Checking participant:`, {
                            participantId,
                            currentUserId,
                            isOtherUser,
                            isOnline
                          });

                          return isOtherUser && isOnline;
                        }) && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">
                        {currentConversation.participants.length} participants
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2"></div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto px-2 sm:px-3 md:px-4 pt-2 pb-1 space-y-1">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-400 mt-8">
                      <ChatAlt2Icon className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((message) => {
                      const senderId =
                        message.sender?.userId?._id || message.sender?.userId;
                      const isOwnMessage =
                        senderId === user._id || senderId === user.id;

                      // Get message content properly
                      let content = "";
                      if (typeof message.content === "string") {
                        content = message.content;
                      } else if (message.content?.text) {
                        content = message.content.text;
                      } else if (message.text) {
                        content = message.text;
                      } else if (message.message) {
                        content = message.message;
                      }

                      // Skip rendering if no valid content
                      if (!content || content.trim() === "") {
                        return null;
                      }

                      return (
                        <div
                          key={message._id}
                          className={`flex ${
                            isOwnMessage ? "justify-end" : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-[80%] sm:max-w-[75%] px-3 py-2 sm:px-4 sm:py-2 rounded-lg ${
                              isOwnMessage
                                ? "bg-[#ffca20] text-black"
                                : "bg-[#2a2a2a] text-white"
                            }`}
                          >
                            {!isOwnMessage && (
                              <div className="text-xs text-gray-400 mb-1">
                                {message.sender?.name || "Unknown User"}
                              </div>
                            )}
                            <div className="text-sm">{content}</div>
                            <div className="flex justify-end mt-1">
                              <span
                                className={`text-[8px] ${
                                  isOwnMessage
                                    ? "text-black/40"
                                    : "text-gray-400"
                                }`}
                              >
                                {formatTimestamp(message.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Typing Indicators */}
                  {Array.from(typingUsers.values()).map((user) => (
                    <div key={user.name} className="flex justify-start">
                      <div className="bg-[#2a2a2a] px-3 py-2 sm:px-4 rounded-lg">
                        <div className="text-xs text-gray-400 mb-1">
                          {user.name}
                        </div>
                        <div className="flex space-x-1">
                          {[0, 0.1, 0.2].map((delay, i) => (
                            <div
                              key={i}
                              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                              style={{ animationDelay: `${delay}s` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="pt-1 pb-2 px-2 sm:px-3 md:px-4 border-t border-[#131313]">
                  <div className="flex items-end space-x-2">
                    {/* Text Input Area */}
                    <div className="flex-1 relative flex">
                      <textarea
                        ref={messageInputRef}
                        value={newMessage}
                        onChange={handleMessageChange}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message..."
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-[#2a2a2a] border border-[#131313] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#ffca20] resize-none"
                        rows="1"
                        style={{ minHeight: "44px", maxHeight: "120px" }}
                      />

                      {/* Attachment Button */}
                      {/* <div className="absolute right-2 bottom-2">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="p-1 text-gray-400 hover:text-white"
                        >
                          <PaperClipIcon className="h-5 w-5" />
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={(e) =>
                            console.log("File selected:", e.target.files[0])
                          }
                        />
                      </div> */}
                    </div>

                    {/* Send Button */}
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || !currentConversation}
                      className="h-[44px] w-[44px] flex items-center justify-center bg-[#ffca20] hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      <PaperAirplaneIcon className="h-5 w-5 transform rotate-90" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div
                className={`flex-1 flex items-center justify-center rounded-lg bg-[#131313] border border-opacity-40 border-white ${
                  isMobile ? "hidden" : ""
                }`}
              >
                <div className="text-center flex flex-col items-center justify-center px-4">
                  <ChatAlt2Icon className="h-16 w-16 text-[#ffca20] mb-4" />
                  <h2 className="text-xl font-medium text-white mb-2">
                    Welcome to Communication Center
                  </h2>
                  <p className="text-gray-400 mb-6 max-w-md">
                    Start a conversation with banking partners or admins, or
                    select an existing chat to continue.
                  </p>
                  <button
                    onClick={() => setShowNewChat(true)}
                    className="bg-[#ffca20] hover:bg-[#ffca20]/80 text-black px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                  >
                    Start New Chat
                    <PlusIcon className="h-5 w-5 ml-2" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New Chat Modal */}
        {showNewChat && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] rounded-lg p-4 md:p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white">
                  New Conversation
                </h3>
                <button
                  onClick={() => setShowNewChat(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Select Banking Partners or Admins
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-[#131313] rounded-lg p-2">
                    {loadingUsers ? (
                      <div className="text-center text-gray-400 py-4">
                        Loading users...
                      </div>
                    ) : userError ? (
                      <div className="text-center text-red-400 py-4">
                        <p className="mb-2">{userError}</p>
                        <button
                          onClick={() => setShowNewChat(false)}
                          className="text-sm text-[#ffca20] hover:text-yellow-400"
                        >
                          Try Again
                        </button>
                      </div>
                    ) : availableUsers.length === 0 ? (
                      <div className="text-center text-gray-400 py-4">
                        No users found
                      </div>
                    ) : (
                      availableUsers.map((user) => (
                        <div
                          key={user._id}
                          onClick={() => {
                            const isSelected = selectedUsers.find(
                              (u) => u._id === user._id
                            );
                            if (isSelected) {
                              setSelectedUsers(
                                selectedUsers.filter((u) => u._id !== user._id)
                              );
                            } else {
                              setSelectedUsers([...selectedUsers, user]);
                            }
                          }}
                          className={`p-2 rounded cursor-pointer transition-colors ${
                            selectedUsers.find((u) => u._id === user._id)
                              ? "bg-[#ffca20] text-white"
                              : "hover:bg-[#2a2a2a] text-gray-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>
                              {user.name}{" "}
                              {user.branchName ? ` - ${user.branchName}` : ""}
                            </span>
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                user.userType === "admin"
                                  ? "bg-red-600 text-white"
                                  : "bg-blue-600 text-white"
                              }`}
                            >
                              {user.userType === "admin"
                                ? "Admin"
                                : "Banking Partner"}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowNewChat(false)}
                    className="flex-1 px-4 py-2 border border-[#131313] text-gray-300 rounded-lg hover:bg-[#2a2a2a] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateConversation}
                    disabled={selectedUsers.length === 0}
                    className="flex-1 px-4 py-2 bg-[#ffca20] hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
            <div className="flex items-center space-x-2">
              <span>{error}</span>
              <button
                onClick={clearError}
                className="text-white hover:text-gray-200"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Footer - Hidden on mobile */}
        <div className="hidden md:flex flex-col sm:flex-row justify-between items-center bg-[#FFCA20]/20 border border-[#FFCA20] rounded-[8px] px-4 py-2 w-full mt-3 box-border gap-2 overflow-hidden">
          <div className="text-sm text-center sm:text-left text-white">
            For Urgent Cases reach out to us at
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 ">
            <a
              href="tel:+917658934523,+919676170926"
              className="flex items-center text-sm text-[#FFCA20]"
            >
              <PhoneIcon className="h-4 w-4 mr-1" />
              +91 7658934523,+91 9676170926
            </a>
            <a
              href="mailto:help@lono.org.in"
              className="flex items-center text-sm text-[#FFCA20]"
            >
              <MailIcon className="h-4 w-4 mr-1" />
              help@lono.org.in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationCenter;
